import React from 'react';
import { Button, Tooltip } from 'antd';
import { DownOutlined, UpOutlined } from '@ant-design/icons';
import SectionPills, { PillItem } from './SectionPills';

export interface ShellSection extends PillItem {
  content: React.ReactNode;
  /** Sections that want the full height for themselves (a chart-only view, a table). */
  hideChart?: boolean;
  /**
   * The section's child manages its own height and scrolling (a data table). It gets a
   * fixed box and the chart stays pinned. Without this the section scrolls as part of the
   * one page column, which is what a long form wants.
   */
  ownScroll?: boolean;
}

/**
 * Mobile layout for the trading pages, replacing the desktop two-column split and the
 * floating-button drawer it degraded into. Top to bottom: an optional page strip
 * (symbol chips), a collapsible chart, the section pills, the active section, and a
 * pinned action bar — so Save/Start are reachable without scrolling a long config to
 * its end, which was the whole problem with the drawer.
 */
const MobileTradingShell: React.FC<{
  topSlot?: React.ReactNode;
  chart?: React.ReactNode;
  /** Chart height as a fraction of the shell, in vh. */
  chartHeightVh?: number;
  sections: ShellSection[];
  activeKey: string;
  onChange: (key: string) => void;
  actionBar?: React.ReactNode;
  /** Persist the chart collapse per page. */
  storageKey?: string;
}> = ({
  topSlot,
  chart,
  chartHeightVh = 32,
  sections,
  activeKey,
  onChange,
  actionBar,
  storageKey,
}) => {
  const [chartCollapsed, setChartCollapsed] = React.useState<boolean>(() => {
    if (!storageKey) return false;
    try {
      return localStorage.getItem(`${storageKey}:chart-collapsed`) === 'true';
    } catch {
      return false;
    }
  });

  const toggleChart = () => {
    setChartCollapsed((prev) => {
      const next = !prev;
      try {
        if (storageKey) localStorage.setItem(`${storageKey}:chart-collapsed`, String(next));
      } catch {
        // Preference just does not survive a reload.
      }
      return next;
    });
  };

  const active = sections.find((s) => s.key === activeKey) ?? sections[0];
  const hasChart = !!chart && !active?.hideChart;
  const showChart = hasChart && !chartCollapsed;
  // `fill` sections hand their child a fixed box to scroll inside (a table that manages its
  // own virtualised scrolling). Everything else scrolls as ONE column: a chart pinned above
  // a separately scrolling form leaves the form a few hundred pixels to work in, which is
  // unusable on a phone — so here the chart scrolls away with the content and the form gets
  // the whole screen.
  const fill = !!active?.ownScroll;

  const scrollerRef = React.useRef<HTMLDivElement>(null);
  // A new section starts at its top, not wherever the previous one was scrolled to.
  React.useEffect(() => {
    scrollerRef.current?.scrollTo?.({ top: 0 });
  }, [activeKey]);

  const chartBlock = hasChart ? (
    <div
      style={{
        flexShrink: 0,
        height: showChart ? `${chartHeightVh}vh` : 0,
        minHeight: showChart ? 180 : 0,
        overflow: 'hidden',
        position: 'relative',
        transition: 'height 0.18s ease, min-height 0.18s ease',
      }}
    >
      {chart}
    </div>
  ) : null;

  const pills = (
    <SectionPills
      items={sections}
      activeKey={active?.key ?? ''}
      onChange={onChange}
      // Stays put while the chart scrolls past it, so switching section never needs a
      // scroll back to the top first.
      sticky={!fill}
      extra={
        hasChart ? (
          <Tooltip title={chartCollapsed ? 'Show chart' : 'Hide chart'}>
            <Button
              type="text"
              size="small"
              aria-label={chartCollapsed ? 'Show chart' : 'Hide chart'}
              icon={chartCollapsed ? <DownOutlined /> : <UpOutlined />}
              onClick={toggleChart}
              style={{ color: '#848E9C', flexShrink: 0 }}
            />
          </Tooltip>
        ) : undefined
      }
    />
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      {topSlot}

      {fill ? (
        <>
          {chartBlock}
          {pills}
          <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {active?.content}
          </div>
        </>
      ) : (
        <div
          ref={scrollerRef}
          style={{ flex: 1, minHeight: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}
        >
          {chartBlock}
          {pills}
          <div style={{ padding: 8 }}>{active?.content}</div>
        </div>
      )}

      {actionBar && (
        <div
          style={{
            flexShrink: 0,
            borderTop: '1px solid #2B3139',
            background: '#15181d',
            padding: '8px',
            paddingBottom: 'calc(8px + env(safe-area-inset-bottom))',
          }}
        >
          {actionBar}
        </div>
      )}
    </div>
  );
};

export default MobileTradingShell;
